import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useAuthStore } from "../stores/authStore";
import { queryOperatorsProtected } from "../lib/operatorsApi";
import type { User } from "../types/database";

// ============================================
// NETWORK USERS (paginated with infinite query)
// ============================================

const USERS_PAGE_SIZE = 50;

interface UsersPageResult {
  users: User[];
  totalCount: number;
  hasMore: boolean;
}

export function useNetworkUsers() {
  const { user, token } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ["network-users"],
    queryFn: async ({ pageParam = 0 }) => {
      if (!token) {
        throw new Error("Not authenticated");
      }

      const offset = pageParam * USERS_PAGE_SIZE;
      const limit = USERS_PAGE_SIZE;

      const result = await queryOperatorsProtected<UsersPageResult>(
        token,
        "list_users",
        {
          offset,
          limit,
        }
      );

      return {
        data: result.users || [],
        totalCount: result.totalCount || 0,
        nextPage: result.hasMore ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: !!user?.id && !!token,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// ============================================
// SEARCH USERS
// ============================================

export function useSearchUsers(searchQuery: string, limit: number = 20) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: ["users-search", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2 || !token) return [];

      return await queryOperatorsProtected<User[]>(token, "search_users", {
        query: searchQuery,
        limit,
      });
    },
    enabled: searchQuery.length >= 2 && !!token,
    staleTime: 30000, // 30 seconds
  });
}
